// src/hooks/useApiError.ts
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import { AxiosError } from 'axios';

// Error types for classification
export type ErrorType =
  | 'network'
  | 'auth'
  | 'permission'
  | 'validation'
  | 'notFound'
  | 'conflict'
  | 'rateLimit'
  | 'server'
  | 'timeout'
  | 'unknown';

// Error classification result
export interface ClassifiedError {
  type: ErrorType;
  message: string;
  title?: string;
  retryable: boolean;
  statusCode?: number;
  details?: Record<string, any>;
}

// API error response structure
interface ApiErrorResponse {
  detail?: string | { msg: string; loc?: string[] }[];
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

/**
 * Custom hook for centralized API error handling
 */
export const useApiError = () => {
  const { t } = useTranslation();
  const { error: showError, warning, info } = useNotification();

  /**
   * Classify an error based on its type and status code
   */
  const classifyError = useCallback((error: any): ClassifiedError => {
    // Network error (no response)
    if (!navigator.onLine) {
      return {
        type: 'network',
        message: t('errors.networkOffline') || 'Pas de connexion internet',
        title: t('errors.networkErrorTitle') || 'Erreur réseau',
        retryable: true,
      };
    }

    // Axios error with response
    if (error?.isAxiosError || error?.response) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;

      // Extract error message from various response formats
      let errorMessage = '';
      if (data?.detail) {
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(d => d.msg).join(', ');
        }
      } else if (data?.message) {
        errorMessage = data.message;
      } else if (data?.error) {
        errorMessage = data.error;
      }

      // Classify by status code
      switch (status) {
        case 400:
          return {
            type: 'validation',
            message: errorMessage || t('errors.validationError') || 'Données invalides',
            title: t('errors.validationTitle') || 'Erreur de validation',
            retryable: false,
            statusCode: status,
            details: data?.errors,
          };

        case 401:
          return {
            type: 'auth',
            message: errorMessage || t('errors.authError') || 'Session expirée, veuillez vous reconnecter',
            title: t('errors.authTitle') || 'Non authentifié',
            retryable: false,
            statusCode: status,
          };

        case 403:
          return {
            type: 'permission',
            message: errorMessage || t('errors.permissionError') || 'Vous n\'avez pas la permission d\'effectuer cette action',
            title: t('errors.permissionTitle') || 'Accès refusé',
            retryable: false,
            statusCode: status,
          };

        case 404:
          return {
            type: 'notFound',
            message: errorMessage || t('errors.notFoundError') || 'Ressource non trouvée',
            title: t('errors.notFoundTitle') || 'Non trouvé',
            retryable: false,
            statusCode: status,
          };

        case 409:
          return {
            type: 'conflict',
            message: errorMessage || t('errors.conflictError') || 'Conflit avec les données existantes',
            title: t('errors.conflictTitle') || 'Conflit',
            retryable: false,
            statusCode: status,
          };

        case 429:
          return {
            type: 'rateLimit',
            message: errorMessage || t('errors.rateLimitError') || 'Trop de requêtes, veuillez patienter',
            title: t('errors.rateLimitTitle') || 'Limite atteinte',
            retryable: true,
            statusCode: status,
          };

        case 500:
        case 502:
        case 503:
        case 504:
          return {
            type: 'server',
            message: errorMessage || t('errors.serverError') || 'Erreur serveur, veuillez réessayer plus tard',
            title: t('errors.serverTitle') || 'Erreur serveur',
            retryable: true,
            statusCode: status,
          };

        default:
          return {
            type: 'unknown',
            message: errorMessage || t('errors.unknownError') || 'Une erreur inattendue est survenue',
            title: t('errors.unknownTitle') || 'Erreur',
            retryable: false,
            statusCode: status,
          };
      }
    }

    // Timeout error
    if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
      return {
        type: 'timeout',
        message: t('errors.timeoutError') || 'La requête a pris trop de temps',
        title: t('errors.timeoutTitle') || 'Délai dépassé',
        retryable: true,
      };
    }

    // Network error without response
    if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
      return {
        type: 'network',
        message: t('errors.networkError') || 'Impossible de contacter le serveur',
        title: t('errors.networkTitle') || 'Erreur réseau',
        retryable: true,
      };
    }

    // Unknown error
    return {
      type: 'unknown',
      message: error?.message || t('errors.unknownError') || 'Une erreur inattendue est survenue',
      title: t('errors.unknownTitle') || 'Erreur',
      retryable: false,
    };
  }, [t]);

  /**
   * Handle an error with appropriate notification
   */
  const handleError = useCallback((error: any, customMessage?: string): ClassifiedError => {
    const classified = classifyError(error);

    // Use custom message if provided
    const message = customMessage || classified.message;

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`[${classified.type.toUpperCase()}]`, message, error);
    }

    // Show appropriate notification based on error type
    switch (classified.type) {
      case 'auth':
        // Auth errors might trigger redirect, show info instead of error
        info(message, classified.title);
        break;

      case 'rateLimit':
      case 'network':
        // Retryable errors shown as warning
        warning(message, classified.title);
        break;

      case 'validation':
        // Validation errors with details
        if (classified.details) {
          const detailMessages = Object.entries(classified.details)
            .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
            .join('\n');
          showError(`${message}\n${detailMessages}`, classified.title);
        } else {
          showError(message, classified.title);
        }
        break;

      default:
        showError(message, classified.title);
    }

    return classified;
  }, [classifyError, showError, warning, info]);

  /**
   * Wrapper for async operations with error handling
   */
  const withErrorHandling = useCallback(<T,>(
    operation: () => Promise<T>,
    options?: {
      errorMessage?: string;
      onError?: (classified: ClassifiedError) => void;
      rethrow?: boolean;
    }
  ): Promise<T | null> => {
    return operation()
      .catch((error) => {
        const classified = handleError(error, options?.errorMessage);
        options?.onError?.(classified);

        if (options?.rethrow) {
          throw error;
        }

        return null;
      });
  }, [handleError]);

  return {
    classifyError,
    handleError,
    withErrorHandling,
  };
};

export default useApiError;
